import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UserLogWithDetail } from "../../../types/users_log.types";
import { UserLogAction } from "../../../types/users_log.types";
import {
  UserRolesTranslations,
  UserStatus,
  UserStatusTranslations,
} from "../../../types/user.types";
import { UsersLogAPI } from "../../../APIs/users_log.api";
import { UsersAPI } from "../../../APIs/users.api";
import { useErrorHandler } from "../../../hooks/useErrorHandler";
import { Loading } from "../../../components/loading";
import { ErrorMessage } from "../../../components/error-message";
import { Button } from "../../../components/button";
import { parseBirthdateToLocalFormat } from "../../../utils/date.utils";
import { UserLogTimelineItem } from "./timeline-item";
import { ConfirmationModal } from "../../../components/delete-confirmation-modal";

export function UsersView() {
  const { id } = useParams<{ id: string }>();
  const [userLogs, setUserLogs] = useState<UserLogWithDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const { error, handleError, clearError } = useErrorHandler();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserLogs = async () => {
      clearError();
      setIsLoading(true);
      try {
        const response = await UsersLogAPI.getUserLog(Number(id));

        if (response.statusCode !== 200) {
          handleError(response.message);
          return;
        }
        setUserLogs(response.data);
      } catch (err) {
        handleError("Error al obtener detalles del usuario");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchUserLogs();
    }
  }, [id, handleError, clearError]);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionColor = (action: UserLogAction) => {
    switch (action) {
      case UserLogAction.CREATION:
        return "#10b981";
      case UserLogAction.ACTIVATION:
        return "#10b981";
      case UserLogAction.DEACTIVATION:
        return "#ef4444";
      case UserLogAction.REACTIVATION:
        return "#10b981";
      case UserLogAction.DELETION:
        return "#ef4444";
      default:
        return "#3b82f6";
    }
  };

  const getActionText = (action: UserLogAction) => {
    switch (action) {
      case UserLogAction.CREATION:
        return "Usuario Creado";
      case UserLogAction.ACTIVATION:
        return "Usuario Activado";
      case UserLogAction.DEACTIVATION:
        return "Usuario Desactivado";
      case UserLogAction.REACTIVATION:
        return "Usuario Reactivado";
      case UserLogAction.DELETION:
        return "Usuario Eliminado";
      default:
        return action;
    }
  };

  const handleResetPassword = async () => {
    if (!id) return;

    setIsResettingPassword(true);
    try {
      const response = await UsersAPI.resetUserPassword(Number(id));

      if (response.statusCode === 200) {
        navigate("/dashboard/new-credentials", {
          state: {
            username: response.data.username,
            password: response.data.password,
            firstName: user.first_name,
            lastName: user.last_name,
          },
        });
      } else {
        handleError(response.message || "Error al restablecer la contraseña");
      }
    } catch (err) {
      handleError("Error al restablecer la contraseña");
    } finally {
      setIsResettingPassword(false);
      setIsResetModalOpen(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="p-8">
        <ErrorMessage message={error} dismissible />
      </div>
    );
  }

  if (!userLogs.length) {
    return (
      <div className="p-8 text-center">
        No se encontraron registros para este usuario
      </div>
    );
  }

  const user = userLogs[0].user;

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <Button
        onClick={() => navigate(-1)}
        text="Volver a la lista"
        variant="back"
        className="mb-6"
      />

      <div className="bg-white rounded-lg shadow-md mb-6 hover:shadow-lg transition-shadow duration-300">
        <div className="p-4 border-b bg-gray-50 rounded-t-lg">
          <h2 className="text-xl font-bold text-gray-800">
            Información del Usuario
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="mb-2">
                <span className="font-semibold text-gray-700">Nombre:</span>{" "}
                <span className="text-gray-800">
                  {user.first_name || user.last_name
                    ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                    : "-"}
                </span>
              </p>
              <p className="mb-2">
                <span className="font-semibold text-gray-700">Email:</span>{" "}
                <span className="text-gray-800">{user.email || "-"}</span>
              </p>
              <p className="mb-2">
                <span className="font-semibold text-gray-700">Rol:</span>{" "}
                <span className="text-gray-800">
                  {UserRolesTranslations[user.role_id]}
                </span>
              </p>
              <p className="mb-2">
                <span className="font-semibold text-gray-700">Usuario:</span>{" "}
                <span className="text-gray-800">{user.username}</span>
              </p>
            </div>
            <div>
              <p className="mb-2">
                <span className="font-semibold text-gray-700">Estado:</span>{" "}
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user.status === UserStatus.ACTIVE
                      ? "bg-green-100 text-green-800"
                      : user.status === UserStatus.REGISTERED
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {UserStatusTranslations[user.status]}
                </span>
              </p>
              <p className="mb-2">
                <span className="font-semibold text-gray-700">DNI:</span>{" "}
                <span className="text-gray-800">{user.dni || "-"}</span>
              </p>
              <p className="mb-2">
                <span className="font-semibold text-gray-700">
                  Fecha de Nacimiento:
                </span>{" "}
                <span className="text-gray-800">
                  {user.birth_date
                    ? parseBirthdateToLocalFormat(String(user.birth_date))
                    : "-"}
                </span>
              </p>
              {user.team_name && (
                <p className="mb-2">
                  <span className="font-semibold text-gray-700">Equipo:</span>{" "}
                  <span className="text-gray-800">{user.team_name}</span>
                </p>
              )}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <Button
              type="button"
              text="Restablecer Contraseña"
              variant="danger"
              onClick={() => setIsResetModalOpen(true)}
              className="w-full md:w-auto"
              disabled={isResettingPassword}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
        <div className="p-4 border-b bg-gray-50 rounded-t-lg">
          <h2 className="text-xl font-bold text-gray-800">
            Historial de Actividad
          </h2>
        </div>
        <div className="p-4">
          <div>
            {userLogs.map((log, index) => (
              <UserLogTimelineItem
                key={log.id}
                log={log}
                user={user}
                isLast={index === userLogs.length - 1}
                formatDate={formatDate}
                getActionText={getActionText}
                getActionColor={getActionColor}
              />
            ))}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isResetModalOpen}
        onClose={() => setIsResetModalOpen(false)}
        title="Confirmar Restablecer Contraseña"
        message="¿Está seguro de que desea restablecer la contraseña para este usuario? Se generará una nueva contraseña aleatoria."
        onConfirm={handleResetPassword}
      />
    </div>
  );
}
